import React, { useState, useEffect } from 'react';
import api from '../api/axios';

interface User { id: number; email: string; full_name: string; role: string; phone?: string; is_active: boolean; created_at: string; }
interface Role { value: string; label: string; }

const roleColors: Record<string, string> = {
  admin: 'bg-red-100 text-red-700', director: 'bg-purple-100 text-purple-700',
  sales_head: 'bg-blue-100 text-blue-700', sales_manager: 'bg-blue-50 text-blue-600',
  service_head: 'bg-orange-100 text-orange-700', service_engineer: 'bg-orange-50 text-orange-600',
  warehouse_head: 'bg-yellow-100 text-yellow-700', logistic_head: 'bg-green-100 text-green-700',
  logist: 'bg-green-50 text-green-600', chief_accountant: 'bg-indigo-100 text-indigo-700',
  accountant: 'bg-indigo-50 text-indigo-600', hr_manager: 'bg-pink-100 text-pink-700',
  lawyer: 'bg-gray-100 text-gray-700',
};

const emptyForm = { full_name: '', email: '', password: '', role: 'sales_manager', phone: '' };

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [newPassword, setNewPassword] = useState('');
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = async () => {
    try { const u = await api.get('/users'); setUsers(u.data); }
    catch (e) { console.error(e); }
    try { const r = await api.get('/roles'); setRoles(r.data); }
    catch (e) { console.error(e); }
  };
  useEffect(() => { fetchData(); }, []);

  const filtered = users.filter(u => {
    const matchSearch = u.full_name.toLowerCase().includes(search.toLowerCase()) || u.email.toLowerCase().includes(search.toLowerCase());
    const matchRole = filterRole ? u.role === filterRole : true;
    return matchSearch && matchRole;
  });

  const getRoleLabel = (role: string) => roles.find(r => r.value === role)?.label || role;

  const openAdd = () => { setForm(emptyForm); setEditId(null); setError(''); setShowModal(true); };
  const openEdit = (u: User) => {
    setForm({ full_name: u.full_name, email: u.email, password: '', role: u.role, phone: u.phone || '' });
    setEditId(u.id); setError(''); setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.full_name || !form.email) { setError("Ism va email kiritilishi kerak"); return; }
    if (!editId && !form.password) { setError("Parol kiritilishi kerak"); return; }
    setLoading(true); setError('');
    try {
      if (editId) await api.put(`/users/${editId}`, { full_name: form.full_name, email: form.email, role: form.role, phone: form.phone || null });
      else await api.post('/users', { full_name: form.full_name, email: form.email, password: form.password, role: form.role, phone: form.phone || null });
      setShowModal(false); fetchData();
    } catch (e: any) { setError(e.response?.data?.detail || 'Xatolik yuz berdi'); }
    finally { setLoading(false); }
  };

  const handleToggleActive = async (user: User) => {
    await api.put(`/users/${user.id}`, { is_active: !user.is_active }); fetchData();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm("O'chirishni tasdiqlaysizmi?")) return;
    try { await api.delete(`/users/${id}`); fetchData(); }
    catch (e: any) { alert(e.response?.data?.detail || 'Xatolik'); }
  };

  const handlePasswordChange = async () => {
    if (!newPassword || newPassword.length < 6) { alert("Parol kamida 6 ta belgidan iborat bo'lishi kerak"); return; }
    try {
      await api.post(`/users/${showPasswordModal}/change-password`, { new_password: newPassword });
      alert("✅ Parol o'zgartirildi"); setShowPasswordModal(null); setNewPassword('');
    } catch (e: any) { alert(e.response?.data?.detail || 'Xatolik'); }
  };

  const roleGroups = [
    { label: 'Rahbariyat', roles: ['admin', 'director'] },
    { label: 'Savdo', roles: ['sales_head', 'sales_manager'] },
    { label: 'Servis', roles: ['service_head', 'service_engineer'] },
    { label: 'Ombor & Logistika', roles: ['warehouse_head', 'logistic_head', 'logist'] },
    { label: 'Moliya', roles: ['chief_accountant', 'accountant'] },
    { label: 'Boshqa', roles: ['hr_manager', 'lawyer'] },
  ];

  const activeCount = users.filter(u => u.is_active).length;
  const inactiveCount = users.filter(u => !u.is_active).length;

  return (
    <div className="p-3 md:p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800">Foydalanuvchilar</h2>
        <button onClick={openAdd} className="px-3 md:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm">
          + <span className="hidden sm:inline">Yangi foydalanuvchi</span>
        </button>
      </div>

      {/* Statistika */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
        <div className="bg-white rounded-xl shadow p-3 md:p-4"><p className="text-gray-500 text-xs md:text-sm">Jami</p><p className="text-xl md:text-2xl font-bold text-gray-800">{users.length}</p></div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4"><p className="text-gray-500 text-xs md:text-sm">Faol</p><p className="text-xl md:text-2xl font-bold text-green-600">{activeCount}</p></div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4"><p className="text-gray-500 text-xs md:text-sm">Bloklangan</p><p className="text-xl md:text-2xl font-bold text-red-500">{inactiveCount}</p></div>
        <div className="bg-white rounded-xl shadow p-3 md:p-4"><p className="text-gray-500 text-xs md:text-sm">Rollar soni</p><p className="text-xl md:text-2xl font-bold text-blue-600">{roles.length}</p></div>
      </div>

      {/* Bo'limlar statistikasi */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-3 mb-4 md:mb-6">
        {roleGroups.map(group => (
          <div key={group.label} className="bg-white rounded-xl shadow p-3 md:p-4">
            <p className="text-gray-600 text-xs md:text-sm font-medium mb-2">{group.label}</p>
            <div className="flex flex-wrap gap-1 md:gap-2">
              {group.roles.map(role => {
                const count = users.filter(u => u.role === role).length;
                return count > 0 ? (
                  <span key={role} className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleColors[role]}`}>
                    {getRoleLabel(role)}: {count}
                  </span>
                ) : null;
              })}
              {group.roles.every(r => users.filter(u => u.role === r).length === 0) && <span className="text-gray-400 text-xs">—</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Filtrlar */}
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <input type="text" placeholder="🔍 Ism yoki email..." value={search} onChange={e => setSearch(e.target.value)}
          className="flex-1 border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <select value={filterRole} onChange={e => setFilterRole(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">Barcha rollar</option>
          {roles.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
      </div>

      {/* Jadval */}
      <div className="bg-white rounded-xl shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
            <tr>
              <th className="px-3 py-3 text-left">Foydalanuvchi</th>
              <th className="px-3 py-3 text-left hidden md:table-cell">Email</th>
              <th className="px-3 py-3 text-left hidden lg:table-cell">Telefon</th>
              <th className="px-3 py-3 text-left">Lavozim</th>
              <th className="px-3 py-3 text-left hidden sm:table-cell">Holat</th>
              <th className="px-3 py-3 text-center">Amallar</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-gray-400">Foydalanuvchilar yo'q</td></tr>
            ) : filtered.map((u, i) => (
              <tr key={u.id} className={`border-t hover:bg-gray-50 ${!u.is_active ? 'opacity-60' : ''}`}>
                <td className="px-3 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 font-bold text-sm">{u.full_name.charAt(0).toUpperCase()}</span>
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-gray-800 truncate">{u.full_name}</div>
                      <div className="md:hidden text-xs text-gray-400 truncate">{u.email}</div>
                      <div className="sm:hidden">
                        <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {u.is_active ? '✅ Faol' : '🚫 Blok'}
                        </span>
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-600 text-xs hidden md:table-cell">{u.email}</td>
                <td className="px-3 py-3 text-gray-600 text-xs hidden lg:table-cell">{u.phone || '—'}</td>
                <td className="px-3 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${roleColors[u.role] || 'bg-gray-100 text-gray-700'}`}>
                    {getRoleLabel(u.role)}
                  </span>
                </td>
                <td className="px-3 py-3 hidden sm:table-cell">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${u.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {u.is_active ? '✅ Faol' : '🚫 Bloklangan'}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => openEdit(u)} className="p-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-xs">✏️</button>
                    <button onClick={() => { setShowPasswordModal(u.id); setNewPassword(''); }} className="p-1.5 bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 text-xs">🔑</button>
                    <button onClick={() => handleToggleActive(u)}
                      className={`p-1.5 rounded text-xs ${u.is_active ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-green-100 text-green-700 hover:bg-green-200'}`}>
                      {u.is_active ? '🚫' : '✅'}
                    </button>
                    <button onClick={() => handleDelete(u.id)} className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200 text-xs">🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-4 md:p-6">
            <h3 className="text-lg md:text-xl font-bold mb-4">{editId ? 'Foydalanuvchini tahrirlash' : 'Yangi foydalanuvchi'}</h3>
            {error && <div className="bg-red-50 text-red-600 p-3 rounded-lg mb-4 text-sm">{error}</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Ism Familiya *</label>
                <input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} placeholder="Abdullayev Abdulla"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@labin.uz"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                <input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+998 90 123 45 67"
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              {!editId && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Parol *</label>
                  <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Kamida 6 ta belgi"
                    className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              )}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Lavozim *</label>
                <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {roleGroups.map(group => (
                    <optgroup key={group.label} label={group.label}>
                      {group.roles.map(role => <option key={role} value={role}>{roles.find(r => r.value === role)?.label || role}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
            {form.role && (
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-gray-500">Tanlangan:</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${roleColors[form.role] || 'bg-gray-100 text-gray-700'}`}>{getRoleLabel(form.role)}</span>
              </div>
            )}
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => { setShowModal(false); setError(''); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor qilish</button>
              <button onClick={handleSubmit} disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm">
                {loading ? 'Saqlanmoqda...' : 'Saqlash'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parol Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
            <h3 className="text-xl font-bold mb-2">Parolni o'zgartirish</h3>
            <p className="text-gray-500 text-sm mb-4">{users.find(u => u.id === showPasswordModal)?.full_name} uchun yangi parol</p>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Yangi parol (kamida 6 belgi)"
              className="w-full border rounded-lg px-3 py-2 mb-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            <div className="flex justify-end gap-3">
              <button onClick={() => { setShowPasswordModal(null); setNewPassword(''); }} className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-sm">Bekor</button>
              <button onClick={handlePasswordChange} className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-sm">🔑 O'zgartirish</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
